import { Button } from "@/components/ui/button";
import { useParallax } from "@/hooks/use-parallax";
import { motion } from "framer-motion";
import { staggerContainer, staggerItem } from "@/lib/animations";

const Hero = () => {
  const parallaxImage = useParallax({ speed: 0.3 });
  const parallaxContent = useParallax({ speed: 0.15 });

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="inicio" className="relative md:min-h-screen flex items-center justify-center md:overflow-hidden">
      {/* Mobile Layout */}
      <div className="md:hidden w-full bg-background">
        <div className="w-full">
          <img
            src="/images/camilla-hero-new.jpg"
            alt="Camilla Felix - Mentora de Automaquiagem"
            className="w-full max-h-[50vh] object-cover object-top block"
            width={1818}
            height={1280}
            loading="eager" />
          
        </div>

        <motion.div
          className="px-5 py-8 relative z-10 text-center bg-background space-y-5"
          variants={staggerContainer}
          initial="hidden"
          animate="visible">
          
          <motion.div
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/10 border border-primary/40"
            variants={staggerItem}>
            
            <span className="text-sm">✨</span>
            <span className="text-xs font-semibold text-primary tracking-wide">
              Aprenda automaquiagem em 30 dias
            </span>
          </motion.div>

          <motion.h1
            className="text-[1.65rem] font-bold leading-[1.18] tracking-tight text-foreground"
            variants={staggerItem}>
            
            A maquiagem que te faz{" "}
            <span className="text-primary">se reconhecer no espelho</span>{" "}
            em 15 minutos.
          </motion.h1>

          <motion.p
            className="text-base font-semibold text-foreground/90"
            variants={staggerItem}>
            
            Mesmo que você nunca tenha acertado nem o básico.
          </motion.p>

          <motion.p
            className="text-sm text-foreground/85 leading-relaxed"
            variants={staggerItem}>
            
            Descubra o método criado por uma mãe de 3 que ensina qualquer mulher a se maquiar sozinha, com leveza, autonomia e confiança. Sem 20 produtos. Sem tutoriais impossíveis. Sem depender de ninguém.
          </motion.p>

          <motion.div
            className="flex items-center justify-center"
            variants={staggerItem}>
            
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="font-medium">⭐ +94 mil mulheres acompanham a Camilla</span>
            </span>
          </motion.div>

          <motion.div variants={staggerItem}>
            <Button
              size="lg"
              onClick={() => scrollToSection("#acesso")}
              className="w-full min-h-[48px] bg-gradient-rose text-primary-foreground shadow-rose hover:shadow-glow hover-lift px-8">
              
              Quero Aprender com a Camilla
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block relative w-full h-screen overflow-hidden">
        <div ref={parallaxImage.ref} style={parallaxImage.style} className="absolute inset-0 scale-110">
          <img
            src="/images/camilla-hero-new.jpg"
            alt="Camilla Felix - Mentora de Automaquiagem"
            className="w-full h-full object-cover object-[50%_20%]"
            width={1818}
            height={1280}
            loading="eager" />
          
        </div>

        <div className="absolute inset-y-0 left-0 w-3/5 bg-gradient-to-r from-background/90 via-background/60 to-transparent" />
        <div className="absolute left-0 inset-y-0 w-1/2 bg-gradient-to-r from-ambient-gold to-transparent opacity-60" />

        <div className="relative z-10 h-full flex items-center pt-20">
          <div className="container mx-auto px-8 lg:px-16">
            <motion.div
              ref={parallaxContent.ref}
              style={parallaxContent.style}
              className="max-w-xl space-y-7"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}>
              
              <motion.div
                className="inline-flex items-center px-6 py-3 rounded-full bg-card-glass/80 backdrop-blur-glass border border-border-gold/40 shadow-rose"
                variants={staggerItem}>
                
                <span className="text-sm font-semibold text-primary tracking-wide">
                  ✨ Aprenda automaquiagem em 30 dias
                </span>
              </motion.div>

              <motion.h1
                className="text-5xl xl:text-6xl leading-[1.1] font-bold tracking-tight text-foreground"
                variants={staggerItem}>
                
                <span className="block">A maquiagem</span>
                <span className="block">
                  <span>que te faz </span>
                  <span className="relative inline text-primary">
                    se reconhecer no espelho
                    <span className="absolute -inset-3 bg-primary/15 blur-2xl -z-10 rounded-full" aria-hidden="true" />
                  </span>
                </span>
                <span className="block">em 15 minutos.</span>
              </motion.h1>

              <motion.p
                className="text-xl font-semibold text-foreground/90 max-w-md"
                variants={staggerItem}>Mesmo que você nunca tenha acertado 
nem o básico.

              </motion.p>

              <motion.p
                className="text-lg text-foreground/80 leading-relaxed font-normal max-w-lg"
                variants={staggerItem}>
                
                Descubra o método criado por uma mãe de 3 que ensina qualquer mulher a se maquiar sozinha, com leveza, autonomia e confiança. Sem 20 produtos. Sem tutoriais impossíveis. Sem depender de ninguém.
              </motion.p>

              <motion.div
                className="flex items-center gap-3"
                variants={staggerItem}>
                
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border-gold/30 backdrop-blur-sm">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-sm font-medium text-foreground/80">⭐ +94 mil mulheres acompanham a Camilla</span>
                </span>
              </motion.div>

              <motion.div
                className="pt-2"
                variants={staggerItem}>
                
                <Button
                  size="xl"
                  variant="premium"
                  onClick={() => scrollToSection("#acesso")}
                  className="text-lg">
                  
                  <span className="relative z-10">Quero Aprender com a Camilla</span>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in z-20">
          <span className="text-xs text-foreground/60 uppercase tracking-widest">Continue</span>
          <div className="w-px h-12 bg-gradient-to-b from-primary to-transparent" />
        </div>
      </div>
    </section>);

};

export default Hero;